import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { API_BASE_URL } from "../lib/env";
import { verifyTurnstileViaWorker } from "../lib/turnstile-siteverify";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  Button,
} from "../components/ui";
import { Seo } from "../lib/head";
import { TurnstileWidget } from "../components/TurnstileWidget";

// The exact consent language the user agrees to. Sent to the API as
// `consent_text` and stored with the opt-in, so we can reproduce what a given
// person saw at the moment they opted in (TCPA evidentiary record).
const CONSENT_TEXT =
  "I agree to receive recurring transactional and account-related SMS messages " +
  "from WeConsult Rx at the mobile number provided (review reminders, license " +
  "renewal and lab-due reminders, bundle signing confirmations, and verification " +
  "codes). Message frequency varies — a typical user receives 0–8 messages per " +
  "week. Message and data rates may apply. Reply STOP to cancel, HELP for help. " +
  "Consent is not a condition of any purchase. Mobile information and messaging " +
  "consent are not shared with third parties or affiliates for marketing or " +
  "promotional purposes.";

const OptInSchema = z.object({
  phone: z.string().min(7, "Please enter your mobile number").max(40),
  email: z.string().email("Please enter a valid email"),
  consent: z.literal(true, {
    errorMap: () => ({ message: "Please check the box to opt in." }),
  }),
  website: z.string().max(200).optional().or(z.literal("")), // honeypot
});

type OptInData = z.infer<typeof OptInSchema>;

const inputCls =
  "block w-full rounded-lg border border-border bg-surface px-3.5 py-3 text-base text-ink-900 placeholder:text-ink-400 focus:border-forest-700 focus:outline-none";

export function SmsOptIn() {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<OptInData>({
    resolver: zodResolver(OptInSchema),
    defaultValues: { website: "" },
  });
  const [status, setStatus] = useState<"idle" | "ok" | "err">("idle");
  const [captchaToken, setCaptchaToken] = useState("");

  async function onSubmit(data: OptInData) {
    const spin = await verifyTurnstileViaWorker(captchaToken);
    if (!spin.success) {
      setStatus("err");
      return;
    }
    try {
      const res = await fetch(`${API_BASE_URL}/v1/marketing/sms-opt-in`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({
          phone: data.phone,
          email: data.email,
          consent: true,
          consent_text: CONSENT_TEXT,
          source: "sms-opt-in-page",
          website: data.website,
          captcha_token: captchaToken,
        }),
      });
      if (!res.ok) throw new Error(`status_${res.status}`);
      setStatus("ok");
      setCaptchaToken("");
    } catch {
      setStatus("err");
    }
  }

  return (
    <>
      <Seo
        title="SMS Opt-In — WeConsult Rx"
        description="Opt in to receive transactional SMS notifications from WeConsult Rx — review reminders, license renewals, and account alerts. No marketing texts."
        path="/sms-opt-in"
      />
      <Section>
        <Container>
          <div className="mx-auto max-w-2xl">
            <Eyebrow>SMS Opt-In</Eyebrow>
            <Heading level={1} className="mt-3">
              Get SMS alerts from WeConsult Rx
            </Heading>
            <Lede className="mt-6">
              Sign up to receive transactional text messages — review reminders,
              license renewal and lab-due reminders, bundle signing
              confirmations, and verification codes. No marketing, no spam.
            </Lede>

            <div className="mt-8 rounded-xl border border-border bg-surface-muted px-5 py-4 text-sm leading-relaxed text-ink-700">
              <p className="mb-2">
                <strong className="text-ink-900">What you&rsquo;ll get:</strong>{" "}
                0&ndash;8 transactional messages per week. Frequency varies with
                your facility&rsquo;s activity.
              </p>
              <p className="mb-2">
                <strong className="text-ink-900">How to stop:</strong> Reply{" "}
                <strong className="text-forest-700">STOP</strong> to any message
                at any time. Reply <strong>HELP</strong> for help.
              </p>
              <p className="mb-0">
                <strong className="text-ink-900">Costs:</strong> Message &amp;
                data rates may apply per your carrier plan. WeConsult Rx does not
                charge for SMS.
              </p>
            </div>

            {status === "ok" ? (
              <Card className="mt-6 border-forest-200 bg-forest-50">
                <div className="flex items-start gap-3">
                  <CheckCircle2
                    className="h-7 w-7 flex-shrink-0 text-forest-700"
                    aria-hidden
                  />
                  <div>
                    <Heading level={3}>You&rsquo;re opted in</Heading>
                    <p className="mt-2 text-ink-700">
                      Check your phone for a confirmation text. Reply{" "}
                      <strong>STOP</strong> at any time to cancel, or{" "}
                      <strong>HELP</strong> for help. You can also email{" "}
                      <a
                        href="mailto:support@weconsultrx.com"
                        className="underline"
                      >
                        support@weconsultrx.com
                      </a>
                      .
                    </p>
                  </div>
                </div>
              </Card>
            ) : (
              <Card className="mt-6">
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  noValidate
                  className="space-y-5"
                >
                  {/* Honeypot */}
                  <label className="sr-only" aria-hidden="true">
                    Website
                    <input
                      type="text"
                      tabIndex={-1}
                      autoComplete="off"
                      {...register("website")}
                      style={{
                        display: "none",
                        position: "absolute",
                        left: "-9999px",
                      }}
                    />
                  </label>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-semibold text-ink-800 mb-1.5"
                    >
                      Mobile phone number
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      inputMode="tel"
                      autoComplete="tel"
                      placeholder="+1 (555) 123-4567"
                      aria-invalid={!!errors.phone}
                      className={inputCls}
                      {...register("phone")}
                    />
                    <p className="mt-1.5 text-xs text-ink-500">
                      US numbers only. We&rsquo;ll send a one-time confirmation
                      text.
                    </p>
                    {errors.phone ? (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.phone.message}
                      </p>
                    ) : null}
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-ink-800 mb-1.5"
                    >
                      Work email
                    </label>
                    <input
                      id="email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@facility.com"
                      aria-invalid={!!errors.email}
                      className={inputCls}
                      {...register("email")}
                    />
                    <p className="mt-1.5 text-xs text-ink-500">
                      Lets us match this opt-in to your WeConsult Rx account.
                    </p>
                    {errors.email ? (
                      <p className="mt-1 text-xs text-red-600">
                        {errors.email.message}
                      </p>
                    ) : null}
                  </div>

                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      className="mt-1 h-5 w-5 rounded border-border accent-forest-700"
                      aria-invalid={!!errors.consent}
                      {...register("consent")}
                    />
                    <span className="text-sm leading-relaxed text-ink-700">
                      {CONSENT_TEXT} See our{" "}
                      <a href="/sms-terms" className="underline">
                        SMS Terms
                      </a>
                      ,{" "}
                      <a href="/privacy" className="underline">
                        Privacy Policy
                      </a>
                      , and{" "}
                      <a href="/terms" className="underline">
                        Terms
                      </a>
                      .
                    </span>
                  </label>
                  {errors.consent ? (
                    <p className="text-xs text-red-600">
                      {errors.consent.message}
                    </p>
                  ) : null}

                  <TurnstileWidget onToken={setCaptchaToken} />

                  <Button type="submit" disabled={isSubmitting} size="lg" className="w-full">
                    {isSubmitting ? "Submitting…" : "Opt in to SMS alerts"}
                  </Button>

                  {status === "err" ? (
                    <p role="alert" className="text-sm text-red-600">
                      Something went wrong. Please try again or email{" "}
                      <a
                        href="mailto:support@weconsultrx.com"
                        className="underline"
                      >
                        support@weconsultrx.com
                      </a>
                      .
                    </p>
                  ) : null}
                </form>
              </Card>
            )}

            <p className="mt-6 text-xs text-ink-500">
              Full terms:{" "}
              <a href="/sms-terms" className="underline">
                SMS Terms
              </a>
              . SMS is delivered via Twilio across AT&amp;T, T-Mobile, Verizon,
              and all major US carriers. For help, email{" "}
              <a href="mailto:support@weconsultrx.com" className="underline">
                support@weconsultrx.com
              </a>
              .
            </p>
          </div>
        </Container>
      </Section>
    </>
  );
}
